/*
 * Cover letter — scroll-sequenced envelope reveal + mouse parallax.
 *
 * Frame sequence: when there are 2+ frame images, scroll progress is mapped from
 * the section's vertical position in the viewport (0 when its center enters at the
 * bottom edge → 1 when its center reaches the viewport middle), so the envelope
 * opens as you scroll and lands on the LAST/open frame exactly when it's centered,
 * then holds. Past `data-reveal` the caption fades in (.is-revealed) and, once the
 * last frame is reached, the card text types on (typewriter, preserving line breaks).
 * Reduced-motion / single-frame: just show the last frame + full caption (no typing).
 *
 * Scroll drift: each decoration with [data-drift] slides horizontally as the
 * section moves through the viewport (centered = no offset), so the clouds glide
 * sideways while you scroll. Runs on all devices (incl. touch).
 *
 * Mouse parallax: each [data-depth] decoration eases toward the pointer by a
 * fraction of its depth (clouds large depth, ornaments small), so they drift at
 * different rates. Skipped on reduced-motion or coarse pointers.
 *
 * Scroll + pointer offsets are composed in applyDecoTransform so they never fight.
 */
class FluevogCoverLetter extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.mobile = window.matchMedia('(pointer: coarse), (max-width: 749px)').matches;

    this.frames = Array.from(this.querySelectorAll('.fv-letter__frame'));
    this.angelFrames = Array.from(this.querySelectorAll('.fv-letter__angel-frame'));
    // Number of distinct angel poses (from data-frame). Poses are spread evenly
    // across the frame sequence, so angels stay in sync for any frame count.
    this.anglePoseCount =
      this.angelFrames.reduce((max, f) => Math.max(max, parseInt(f.dataset.frame, 10) + 1), 0) || 1;
    this.decorations = Array.from(this.querySelectorAll('[data-depth]'));
    this.cardtext = this.querySelector('.fv-letter__cardtext');
    this.typed = false;
    this.canType = false;
    this.inView = false;
    this.strength = (parseFloat(this.dataset.strength) || 100) / 100;
    this.revealStart = (parseFloat(this.dataset.reveal) || 70) / 100;

    this.initDecorations();
    this.setupSequence();
    this.setupParallax();
  }

  /* Type the card text out character-by-character, preserving line breaks. */
  typeText() {
    const el = this.cardtext;
    if (!el) return;
    const text = el.dataset.text || el.textContent || '';
    el.textContent = '';
    el.classList.add('is-typing');

    let i = 0;
    const step = () => {
      if (i >= text.length) {
        el.classList.remove('is-typing');
        return;
      }
      const ch = text[i++];
      el.appendChild(ch === '\n' ? document.createElement('br') : document.createTextNode(ch));
      this._typeTimer = window.setTimeout(step, ch === '\n' ? 220 : 55);
    };
    step();
  }

  /* Cache per-decoration motion params. Centering + rotation live in CSS; JS only
     feeds the dynamic offset via --fv-shift-x/y so the two never fight. */
  initDecorations() {
    this.decorations.forEach((item) => {
      item._drift = parseFloat(item.dataset.drift || '0');
      item._sx = 0;
      item._mx = 0;
      item._my = 0;
    });
  }

  /* Compose scroll drift (sx) + pointer parallax (mx/my) into the shift vars. */
  applyDecoTransform(item) {
    item.style.setProperty('--fv-shift-x', `${(item._sx + item._mx).toFixed(2)}px`);
    item.style.setProperty('--fv-shift-y', `${item._my.toFixed(2)}px`);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.removeEventListener('pointermove', this.onMove);
    this.removeEventListener('pointerleave', this.onLeave);
    this.io?.disconnect();
    if (this._typeTimer) window.clearTimeout(this._typeTimer);
  }

  /* ---- Scroll-sequenced frame reveal ---- */
  setupSequence() {
    const scrubbable = this.frames.length > 1 && !this.reducedMotion;

    if (!scrubbable) {
      // Static end state: last frame + full caption visible (no animation).
      this.setActiveFrame(this.frames.length - 1);
      this.classList.add('is-revealed', 'is-drawn');
      return;
    }

    // Start the caption empty so it can type on when the last frame is reached.
    this.canType = true;
    if (this.cardtext) this.cardtext.textContent = '';

    this.activeIndex = -1;
    this.scrollTicking = false;
    this.onScroll = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Only run scroll work while the section is near the viewport.
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.inView = entries.some((e) => e.isIntersecting);
          if (this.inView) this.onScroll();
        },
        { rootMargin: '25% 0px' }
      );
      this.io.observe(this);
    } else {
      this.inView = true;
    }

    this.onScroll();
  }

  onScroll() {
    if (this.scrollTicking || !this.inView) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      this.scrollTicking = false;
      this.updateSequence();
    });
  }

  updateSequence() {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const rect = this.getBoundingClientRect();

    // Map the section's center position to 0→1: 0 when its center is at the bottom
    // edge of the viewport, 1 when its center reaches the viewport middle. The last
    // frame is reached when the envelope is centered, then held as you scroll past.
    const center = rect.top + rect.height / 2;
    const progress = Math.max(0, Math.min(1, (vh - center) / (vh / 2)));

    const index = Math.round(progress * (this.frames.length - 1));
    this.setActiveFrame(index);
    this.classList.toggle('is-revealed', progress >= this.revealStart);

    // Cloud drift is desktop-only — horizontal transforms during iOS momentum scroll jank.
    if (this.mobile) return;

    const driftT = (vh / 2 - center) / vh;
    this.decorations.forEach((item) => {
      if (!item._drift) return;
      item._sx = driftT * item._drift;
      this.applyDecoTransform(item);
    });
  }

  setActiveFrame(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.frames.forEach((frame, i) => frame.classList.toggle('is-active', i === index));

    // Cross-fade the angels to the pose for this frame. The 3 poses are mapped
    // proportionally across the frame sequence, so they hold across a range of
    // frames instead of only matching a single index (works for 3 or 8+ frames).
    const total = this.frames.length;
    const pose =
      total > 1 ? Math.min(this.anglePoseCount - 1, Math.floor((index / total) * this.anglePoseCount)) : 0;
    this.angelFrames.forEach((frame) => {
      frame.classList.toggle('is-active', parseInt(frame.dataset.frame, 10) === pose);
    });

    // Animate the writing on once the last (open) frame is reached: draw-on for the
    // handwritten SVG (.is-drawn) and/or type-on for the live text fallback.
    if (this.canType && !this.typed && index === this.frames.length - 1) {
      this.typed = true;
      this.classList.add('is-drawn');
      this.typeText();
    }
  }

  /* ---- Pointer parallax for clouds / ornaments ---- */
  setupParallax() {
    if (this.reducedMotion || !this.finePointer || this.decorations.length === 0) return;

    this.targetX = 0;
    this.targetY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.parallaxRunning = false;

    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.tick = this.tick.bind(this);

    this.addEventListener('pointermove', this.onMove, { passive: true });
    this.addEventListener('pointerleave', this.onLeave, { passive: true });
  }

  onMove(event) {
    const rect = this.getBoundingClientRect();
    this.targetX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    this.targetY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    this.startParallax();
  }

  onLeave() {
    this.targetX = 0;
    this.targetY = 0;
    this.startParallax();
  }

  startParallax() {
    if (this.parallaxRunning) return;
    this.parallaxRunning = true;
    requestAnimationFrame(this.tick);
  }

  tick() {
    this.currentX += (this.targetX - this.currentX) * 0.08;
    this.currentY += (this.targetY - this.currentY) * 0.08;

    this.decorations.forEach((item) => {
      const depth = parseFloat(item.dataset.depth || '0') * this.strength;
      item._mx = -this.currentX * depth;
      item._my = -this.currentY * depth;
      this.applyDecoTransform(item);
    });

    const settled =
      Math.abs(this.targetX - this.currentX) < 0.001 &&
      Math.abs(this.targetY - this.currentY) < 0.001;

    if (settled) {
      this.parallaxRunning = false;
    } else {
      requestAnimationFrame(this.tick);
    }
  }
}

if (!customElements.get('fv-cover-letter')) {
  customElements.define('fv-cover-letter', FluevogCoverLetter);
}
